/**
 * Tenant Workspace API — tenant-scoped operations.
 *
 * These endpoints require a valid tenant membership and the appropriate
 * vendor.* permissions. They operate on the CURRENT tenant resolved from
 * the caller's session (via /api/v1/tenants/current/*).
 *
 * CONTRACT SOURCE: contracts/vendor-profile.register.md, contracts/foundation.register.md
 * All schemas mirror the API contract and are validated on every response.
 *
 * ---------------------------------------------------------------------------
 * THE API BASE IS NOT PART OF A PATH
 * ---------------------------------------------------------------------------
 * `http` resolves every path against the API BASE (`/api/v1`, see
 * `api/config/env.ts`). A path written here as `/api/v1/...` is resolved a
 * SECOND time and leaves the browser asking for `/api/v1/api/v1/...` — a
 * route that does not exist. The bases below are therefore RELATIVE to the
 * API base: `/tenants/current/...` reaches `/api/v1/tenants/current/...`.
 *
 * ---------------------------------------------------------------------------
 * WHERE A LOCATION LIVES
 * ---------------------------------------------------------------------------
 * A location is a VENDOR-PROFILE resource, and its canonical surface is the
 * vendor-profile-scoped one: it is created under a profile, listed under that
 * profile and published through that profile. The tenant-scoped onboarding
 * surface (`/tenants/{tenantId}/locations`) is the PLATFORM operator's route —
 * it belongs to `platform-onboarding-api.ts` — and it records no coordinate,
 * which is the one thing a location needs before discovery can place it.
 */
import { http, generateIdempotencyKey } from '@/api/client/http';
import { z } from 'zod';
import { CollectionResponseSchema, IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

// ============================================================================
// Shared Schemas
// ============================================================================

export const BusinessProfileSchema = z.object({
  tenantId: UuidSchema,
  publicName: z.string().min(1).max(120),
  legalName: z.string().min(1).max(200).nullable(),
  description: z.string().min(1).max(2000).nullable(),
  businessType: z.string().nullable(),
  establishmentYear: z.number().int().nullable(),
  primaryCategory: z.string().nullable(),
  websiteUrl: z.string().url().nullable(),
  registrationStatus: z.enum(['unregistered', 'pending', 'registered']).nullable(),
  primaryEmail: z.string().email().nullable(),
  businessPhone: z.string().nullable(),
  whatsappNumber: z.string().nullable(),
  supportEmail: z.string().email().nullable(),
  alternateContact: z.string().nullable(),
  contactVisibility: z.record(z.string(), z.boolean()),
  // The tenant-profile surface does not carry the logo id — it is read from
  // the vendor profile's branding. Optional here so the absent field is not
  // mistaken for a contract mismatch.
  logoDocumentId: UuidSchema.nullable().optional(),
  bannerDocumentId: UuidSchema.nullable(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable(),
  version: z.number().int().min(1),
});

export type BusinessProfile = z.infer<typeof BusinessProfileSchema>;

// Profile patch schema - only fields that can be updated via PATCH /profile
// logoDocumentId, bannerDocumentId, primaryColor have separate endpoints
export const BusinessProfilePatchSchema = z
  .object({
    publicName: z.string().min(1).max(120).optional(),
    legalName: z.string().min(1).max(200).nullable().optional(),
    description: z.string().min(1).max(2000).nullable().optional(),
    businessType: z.string().nullable().optional(),
    establishmentYear: z.number().int().nullable().optional(),
    primaryCategory: z.string().nullable().optional(),
    websiteUrl: z.string().url().nullable().optional(),
    registrationStatus: z.enum(['unregistered', 'pending', 'registered']).nullable().optional(),
    primaryEmail: z.string().email().nullable().optional(),
    businessPhone: z.string().nullable().optional(),
    whatsappNumber: z.string().nullable().optional(),
    supportEmail: z.string().email().nullable().optional(),
    alternateContact: z.string().nullable().optional(),
    contactVisibility: z.record(z.string(), z.boolean()).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'an update must carry at least one writable field',
  });

export type BusinessProfilePatch = z.infer<typeof BusinessProfilePatchSchema>;

export const BusinessProfileResponseSchema = z.object({
  data: BusinessProfileSchema,
  meta: z.object({ requestId: z.string().max(128) }),
});

export type BusinessProfileResponse = z.infer<typeof BusinessProfileResponseSchema>;

// ============================================================================
// Verticals
// ============================================================================

export const AvailableVerticalSchema = z.object({
  code: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
});

export type AvailableVertical = z.infer<typeof AvailableVerticalSchema>;

export const TenantVerticalSchema = z.object({
  verticalCode: z.string(),
  name: z.string(),
  isPrimary: z.boolean(),
  status: z.enum(['active', 'disabled']),
  assignedAt: IsoDateTimeSchema,
});

export type TenantVertical = z.infer<typeof TenantVerticalSchema>;

export const AssignVerticalRequestSchema = z.object({
  verticalCode: z.string(),
  isPrimary: z.boolean().optional(),
});

export type AssignVerticalRequest = z.infer<typeof AssignVerticalRequestSchema>;

export const UpdateVerticalRequestSchema = z
  .object({
    status: z.enum(['active', 'disabled']).optional(),
    isPrimary: z.boolean().optional(),
  })
  .refine((v) => !(v.status !== undefined && v.isPrimary === true), {
    message: 'set status OR primary, not both',
  });

export type UpdateVerticalRequest = z.infer<typeof UpdateVerticalRequestSchema>;

export const VerticalListResponseSchema = z.object({
  data: z.array(TenantVerticalSchema),
  meta: z.object({ requestId: z.string().max(128) }),
});

export type VerticalListResponse = z.infer<typeof VerticalListResponseSchema>;

export const AvailableVerticalsResponseSchema = z.object({
  data: z.array(AvailableVerticalSchema),
  meta: z.object({ requestId: z.string().max(128) }),
});

export type AvailableVerticalsResponse = z.infer<typeof AvailableVerticalsResponseSchema>;

export const SingleVerticalResponseSchema = z.object({
  data: TenantVerticalSchema,
  meta: z.object({ requestId: z.string().max(128) }),
});

export type SingleVerticalResponse = z.infer<typeof SingleVerticalResponseSchema>;

// ============================================================================
// Vendor Profile & Locations
//
// CONTRACT SOURCE: the API's vendor-profile contracts (`m09-vendor-profile`).
// A location belongs to a VENDOR PROFILE: it is created under one, listed
// under one and published through one.
// ============================================================================

export const PublicationStatusSchema = z.enum(['draft', 'published', 'archived']);
export type PublicationStatus = z.infer<typeof PublicationStatusSchema>;

export const VendorProfileSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  publicName: z.string().min(1).max(120),
  legalName: z.string().min(1).max(200),
  dbaName: z.string().min(1).max(120).nullable(),
  description: z.string().min(1).max(2000).nullable(),
  branding: z.object({
    logoDocumentId: UuidSchema.nullable(),
    primaryColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable(),
  }),
  publicationStatus: PublicationStatusSchema,
  locationCount: z.number().int().min(0),
  version: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export type VendorProfile = z.infer<typeof VendorProfileSchema>;

export const VendorProfileListResponseSchema = CollectionResponseSchema(VendorProfileSchema);

export const VendorProfileResponseSchema = z.object({
  data: VendorProfileSchema,
  meta: z.object({ requestId: z.string().max(128) }),
});

export const CoordinatesSchema = z.object({
  latitude: z.number().gte(-90).lte(90),
  longitude: z.number().gte(-180).lte(180),
});

export type Coordinates = z.infer<typeof CoordinatesSchema>;

export const VendorContactSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('email'),
    value: z.string().min(1).max(254),
    visibility: z.enum(['public', 'private']),
  }),
  z.object({
    kind: z.literal('phone'),
    value: z.string().min(1).max(254),
    visibility: z.enum(['public', 'private']),
  }),
]);

export const PrivateAddressSchema = z.object({
  addressLines: z.array(z.string().min(1).max(200)).min(1).max(5),
  country: z.string().length(2),
  region: z.string().max(120).nullable(),
  city: z.string().max(120).nullable(),
  postalCode: z.string().max(16).nullable(),
  coordinates: CoordinatesSchema.nullable(),
});

export type PrivateAddress = z.infer<typeof PrivateAddressSchema>;

export const VendorLocationSchema = z.object({
  id: UuidSchema,
  vendorProfileId: UuidSchema,
  tenantId: UuidSchema,
  label: z.string().min(1).max(80),
  privateAddress: PrivateAddressSchema,
  timezone: z.string().min(1).max(64),
  contacts: z.array(VendorContactSchema),
  publicationStatus: PublicationStatusSchema,
  displayRegion: z.string().max(120).nullable(),
  publishedAt: IsoDateTimeSchema.nullable(),
  version: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export type VendorLocation = z.infer<typeof VendorLocationSchema>;

/**
 * The tenant-scoped location shape — the flat row the location-management
 * surface returns: the label, the address detail and the PRIVATE point.
 *
 * It is not the vendor-profile shape above: that one nests the private
 * address under `privateAddress` and carries contacts and timezone. The two
 * surfaces describe the same row differently, and a client must not pretend
 * they are the same contract.
 */
export const TenantLocationSchema = z.object({
  id: UuidSchema,
  label: z.string(),
  locationType: z.string().nullable(),
  isPrimary: z.boolean(),
  isCustomerFacing: z.boolean(),
  publicationStatus: PublicationStatusSchema,
  country: z.string(),
  displayRegion: z.string().nullable(),
  addressLines: z.array(z.string()),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  version: z.number().int(),
});

export type TenantLocation = z.infer<typeof TenantLocationSchema>;

export const TenantLocationResponseSchema = z.object({
  data: TenantLocationSchema,
  meta: z.object({ requestId: z.string() }),
});

/**
 * What an operator may change on a location.
 *
 * A coordinate arrives as a PAIR or not at all: the API refuses half of one,
 * and the UI refuses to send it.
 */
export const UpdateLocationRequestSchema = z.object({
  label: z.string().min(1).max(80).optional(),
  addressLines: z.array(z.string().min(1).max(200)).min(1).max(5).optional(),
  postalCode: z.string().max(16).optional(),
  city: z.string().max(120).optional(),
  region: z.string().max(120).optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export type UpdateLocationRequest = z.infer<typeof UpdateLocationRequestSchema>;

export const CreateVendorLocationRequestSchema = z.object({
  label: z.string().min(1).max(80),
  privateAddress: z.object({
    addressLines: z.array(z.string().min(1).max(200)).min(1).max(5),
    country: z.string().length(2),
    region: z.string().max(120).optional(),
    city: z.string().max(120).optional(),
    postalCode: z.string().max(16).optional(),
    coordinates: CoordinatesSchema.optional(),
  }),
  timezone: z.string().min(1).max(64),
  contacts: z.array(VendorContactSchema).optional(),
});

export type CreateVendorLocationRequest = z.infer<typeof CreateVendorLocationRequestSchema>;

export const PublishVendorLocationRequestSchema = z.object({
  displayRegion: z.string().min(1).max(120),
  approximateCoordinates: CoordinatesSchema.optional(),
});

export type PublishVendorLocationRequest = z.infer<typeof PublishVendorLocationRequestSchema>;

export const VendorLocationListResponseSchema = CollectionResponseSchema(VendorLocationSchema);

export const VendorLocationResponseSchema = z.object({
  data: VendorLocationSchema,
  meta: z.object({ requestId: z.string().max(128) }),
});

export type VendorLocationResponse = z.infer<typeof VendorLocationResponseSchema>;

// ============================================================================
// Coverage (the API calls it Service Areas)
//
// CONTRACT SOURCE: the API's service-area contracts (`m01-tenancy`).
// Coverage belongs to the CURRENT TENANT and points at a location. It is
// either POSTAL (a postal code) or RADIUS (a metre radius around the
// location's point).
// ============================================================================

export const FulfillmentModeSchema = z.enum(['at_customer', 'delivery']);
export type FulfillmentMode = z.infer<typeof FulfillmentModeSchema>;

export const CoverageTypeSchema = z.enum(['postal', 'radius']);
export type CoverageType = z.infer<typeof CoverageTypeSchema>;

export const ServiceAreaSchema = z.object({
  id: UuidSchema,
  vendorLocationId: UuidSchema,
  coverageType: CoverageTypeSchema,
  postalCode: z.string().max(16).nullable(),
  radiusMeters: z.number().int().nullable(),
  country: z.string().length(2),
  fulfillmentModes: z.array(FulfillmentModeSchema),
  status: z.enum(['active', 'disabled']),
});

export type ServiceArea = z.infer<typeof ServiceAreaSchema>;

export const AssignServiceAreaRequestSchema = z.object({
  locationId: UuidSchema,
  coverageType: CoverageTypeSchema,
  postalCode: z.string().max(16).nullish(),
  radiusMeters: z.number().int().positive().nullish(),
  modes: z.array(FulfillmentModeSchema).min(1),
});

export type AssignServiceAreaRequest = z.infer<typeof AssignServiceAreaRequestSchema>;

export const UpdateServiceAreaRequestSchema = z
  .object({
    modes: z.array(FulfillmentModeSchema).min(1).optional(),
    radiusMeters: z.number().int().positive().optional(),
    status: z.enum(['active', 'disabled']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'an update must carry at least one field',
  });

export type UpdateServiceAreaRequest = z.infer<typeof UpdateServiceAreaRequestSchema>;

/**
 * The coverage write routes answer with the id of the row they touched, not
 * with the row itself — so a write is acknowledged, and the row is read back
 * from the collection.
 */
export const ServiceAreaIdResponseSchema = z.object({
  data: z.object({ id: UuidSchema }),
  meta: z.object({ requestId: z.string().max(128) }),
});

export type ServiceAreaIdResponse = z.infer<typeof ServiceAreaIdResponseSchema>;

export const ServiceAreaListResponseSchema = z.object({
  data: z.array(ServiceAreaSchema),
  meta: z.object({ requestId: z.string().max(128) }),
});

export type ServiceAreaListResponse = z.infer<typeof ServiceAreaListResponseSchema>;

export const ServiceabilityResultSchema = z.enum([
  'serviceable',
  'not_serviceable',
  'insufficient_geography',
]);
export type ServiceabilityResult = z.infer<typeof ServiceabilityResultSchema>;

export const ServiceabilitySchema = z.object({
  result: ServiceabilityResultSchema,
  matchedBy: z.string().nullable(),
});

export type Serviceability = z.infer<typeof ServiceabilitySchema>;

export const CheckServiceabilityRequestSchema = z.object({
  mode: FulfillmentModeSchema,
  postalCode: z.string(),
  locationId: UuidSchema.optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export type CheckServiceabilityRequest = z.infer<typeof CheckServiceabilityRequestSchema>;

export const ServiceabilityResponseSchema = z.object({
  data: ServiceabilitySchema,
  meta: z.object({ requestId: z.string().max(128) }),
});

export type ServiceabilityResponse = z.infer<typeof ServiceabilityResponseSchema>;

// ============================================================================
// Branding
// ============================================================================

export const AttachMediaRequestSchema = z.object({
  documentId: UuidSchema.nullable(),
});

export type AttachMediaRequest = z.infer<typeof AttachMediaRequestSchema>;

export const AttachMediaResponseSchema = z.object({
  data: z.object({ documentId: UuidSchema.nullable() }),
  meta: z.object({ requestId: z.string().max(128) }),
});

export type AttachMediaResponse = z.infer<typeof AttachMediaResponseSchema>;

export const PrimaryColorRequestSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
});

export type PrimaryColorRequest = z.infer<typeof PrimaryColorRequestSchema>;

// ============================================================================
// Onboarding Readiness
// ============================================================================

export const OnboardingCategorySchema = z.enum([
  'business_information',
  'verticals',
  'contacts',
  'locations',
  'legal_verification',
  'branding',
  'website',
  'social',
  'marketing',
  'operating_hours',
  'catalog',
  'payments',
  'subscription',
  'staff',
  'communications',
  'policies',
  'integrations',
  'media',
  'fulfilment',
  'visibility',
]);

export type OnboardingCategory = z.infer<typeof OnboardingCategorySchema>;

export const OnboardingCategoryStateSchema = z.object({
  category: OnboardingCategorySchema,
  status: z.enum(['complete', 'missing', 'not_supported']),
  reason: z.string().optional(),
});

export type OnboardingCategoryState = z.infer<typeof OnboardingCategoryStateSchema>;

export const OnboardingReadinessSchema = z.object({
  tenantId: UuidSchema,
  overall: z.enum(['complete', 'incomplete']),
  missingCount: z.number().int().min(0),
  categories: z.array(OnboardingCategoryStateSchema),
});

export type OnboardingReadiness = z.infer<typeof OnboardingReadinessSchema>;

export const OnboardingReadinessResponseSchema = z.object({
  data: OnboardingReadinessSchema,
  meta: z.object({ requestId: z.string().max(128) }),
});

export type OnboardingReadinessResponse = z.infer<typeof OnboardingReadinessResponseSchema>;

// ============================================================================
// API Client
// ============================================================================

/**
 * The tenant-scoped base, RELATIVE to the API base (`/api/v1`).
 * `/tenants/current/...` reaches `/api/v1/tenants/current/...`.
 */
const BASE = '/tenants/current';

/**
 * The vendor-profile-scoped base, RELATIVE to the API base.
 * `/vendor/vendor-profiles/...` reaches `/api/v1/vendor/vendor-profiles/...`.
 */
const VENDOR_PROFILES = '/vendor/vendor-profiles';

export const TenantWorkspaceApi = {
  // Business Profile
  async getProfile(): Promise<BusinessProfile> {
    const response = await http.get(`${BASE}/profile`, BusinessProfileResponseSchema);
    return response.data;
  },

  async updateProfile(patch: BusinessProfilePatch): Promise<BusinessProfile> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.patch(`${BASE}/profile`, BusinessProfileResponseSchema, {
      body: patch,
      idempotencyKey,
    });
    return response.data;
  },

  // Verticals
  async listAvailableVerticals(): Promise<AvailableVertical[]> {
    const response = await http.get(
      `${BASE}/verticals/available`,
      AvailableVerticalsResponseSchema,
    );
    return response.data;
  },

  async listAssignedVerticals(): Promise<TenantVertical[]> {
    const response = await http.get(`${BASE}/verticals`, VerticalListResponseSchema);
    return response.data;
  },

  async assignVertical(input: AssignVerticalRequest): Promise<TenantVertical> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.post(`${BASE}/verticals`, SingleVerticalResponseSchema, {
      body: input,
      idempotencyKey,
    });
    return response.data;
  },

  async updateVertical(
    verticalCode: string,
    input: UpdateVerticalRequest,
  ): Promise<TenantVertical> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.patch(
      `${BASE}/verticals/${verticalCode}`,
      SingleVerticalResponseSchema,
      {
        body: input,
        idempotencyKey,
      },
    );
    return response.data;
  },

  // Vendor Profile & Locations (canonical: vendor-profile-scoped)
  async listVendorProfiles(): Promise<VendorProfile[]> {
    const response = await http.get(VENDOR_PROFILES, VendorProfileListResponseSchema);
    return response.data;
  },

  async listLocations(vendorProfileId: string): Promise<VendorLocation[]> {
    const response = await http.get(
      `${VENDOR_PROFILES}/${vendorProfileId}/locations`,
      VendorLocationListResponseSchema,
    );
    return response.data;
  },

  async createLocation(
    vendorProfileId: string,
    input: CreateVendorLocationRequest,
  ): Promise<VendorLocation> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.post(
      `${VENDOR_PROFILES}/${vendorProfileId}/locations`,
      VendorLocationResponseSchema,
      { body: input, idempotencyKey },
    );
    return response.data;
  },

  /**
   * Publishing is what makes a location publicly discoverable.
   *
   * The route is optimistic: it carries the location's version in `If-Match`
   * (a BARE integer, not an ETag) and refuses a stale one.
   */
  async publishLocation(
    vendorProfileId: string,
    locationId: string,
    input: PublishVendorLocationRequest,
    version: number,
  ): Promise<VendorLocation> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.post(
      `${VENDOR_PROFILES}/${vendorProfileId}/locations/${locationId}/publish`,
      VendorLocationResponseSchema,
      { body: input, idempotencyKey, ifMatch: version, ifMatchFormat: 'bare-version' },
    );
    return response.data;
  },

  /**
   * Completes or corrects a location.
   *
   * The canonical CREATE and PUBLISH live on the vendor-profile-scoped surface,
   * which records the coordinate at creation. This update is the TENANT-scoped
   * one — the same surface the coverage form already uses — and it is how a
   * location that was created without a point (a legacy record) is completed:
   * the operator gives it the city, the postal code and the coordinate that
   * discovery needs to place it.
   */
  async updateLocation(locationId: string, input: UpdateLocationRequest): Promise<TenantLocation> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.patch(
      `${BASE}/locations/${locationId}`,
      TenantLocationResponseSchema,
      { body: input, idempotencyKey },
    );
    return response.data;
  },

  // Coverage
  async listServiceAreas(locationId?: string): Promise<ServiceArea[]> {
    const query = locationId ? { locationId } : undefined;
    const response = await http.get(`${BASE}/service-areas`, ServiceAreaListResponseSchema, {
      query,
    });
    return response.data;
  },

  async checkServiceability(input: CheckServiceabilityRequest): Promise<Serviceability> {
    const response = await http.post(`${BASE}/service-areas/check`, ServiceabilityResponseSchema, {
      body: input,
    });
    return response.data;
  },

  async assignServiceArea(input: AssignServiceAreaRequest): Promise<{ id: string }> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.post(`${BASE}/service-areas`, ServiceAreaIdResponseSchema, {
      body: input,
      idempotencyKey,
    });
    return response.data;
  },

  /**
   * One route updates a coverage row: its modes, its radius and its status.
   * It answers with the row's id, so the caller refreshes the collection.
   */
  async updateServiceArea(
    areaId: string,
    input: UpdateServiceAreaRequest,
  ): Promise<{ id: string }> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.patch(
      `${BASE}/service-areas/${areaId}`,
      ServiceAreaIdResponseSchema,
      { body: input, idempotencyKey },
    );
    return response.data;
  },

  // Branding
  async attachLogo(documentId: string | null): Promise<{ documentId: string | null }> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.post(`${BASE}/logo`, AttachMediaResponseSchema, {
      body: { documentId },
      idempotencyKey,
    });
    return response.data;
  },

  async attachBanner(documentId: string | null): Promise<{ documentId: string | null }> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.post(`${BASE}/banner`, AttachMediaResponseSchema, {
      body: { documentId },
      idempotencyKey,
    });
    return response.data;
  },

  async setPrimaryColor(primaryColor: string): Promise<BusinessProfile> {
    const idempotencyKey = generateIdempotencyKey();
    const response = await http.patch(`${BASE}/branding/color`, BusinessProfileResponseSchema, {
      body: { primaryColor },
      idempotencyKey,
    });
    return response.data;
  },

  // Onboarding Readiness
  async getOnboardingReadiness(): Promise<OnboardingReadiness> {
    const response = await http.get(`${BASE}/onboarding`, OnboardingReadinessResponseSchema);
    return response.data;
  },
};
